<template>
  <Loader v-if="loading" />
  <template v-else>
    <!-- Inspection history -->
    <DashboardChartPanel
      v-if="isShaftV2"
      :showTitleOnMobile="true"
      :title="$t('shafts.inspection_history')"
    >
      <StackedChart
        v-if="inspectionHistory && !inspectionHistoryLoading"
        id="history"
        :height="200"
        :series="inspectionHistory"
      />
      <Loader
        v-if="inspectionHistoryLoading"
        class="chart-loader-history"
      />
    </DashboardChartPanel>

    <div class="chart-container">
      <div class="chart-selector typo-small-title hide-for-medium">
        <SwitchButton
          v-model="graph"
          :items="graphOptions"
          :modifiers="['soft', 'lighter']"
          name="graph"
        />
      </div>
      <!-- Issues by condition -->
      <DashboardChartPanel
        :class="{'show-for-medium': graph !== 'condition'}"
        :title="$t('operations.issues_by_condition')"
      >
        <template #panelHeaderActions>
          <DropdownLink
            v-if="earliestApprovedInspection && conditionParams"
            :items="[{ label: $t('general.csv'), path: conditionParams }]"
            :modifiers="['right-border', !conditionData && 'hide']"
          />
          <DateFilter
            v-if="earliestApprovedInspection"
            id="condition"
            :class="!conditionData && 'hide'"
            :earliestGraphDate="earliestApprovedInspection"
            :modifiers="['no-padding-right']"
            :showCustomDateRange="true"
            @update:model-value="filterGraph($event)"
          />
        </template>
        <DashboardChart
          v-if="conditionData"
          chartStyle="condition"
          :series="conditionData"
          :showLegend="isShaftV2"
          @CSVparamUpdate="updateCSVPath"
        />
        <Loader
          v-else
          class="chart-loader-issue"
        />
      </DashboardChartPanel>
      <!-- Issues by risk / priority rating -->
      <DashboardChartPanel
        :class="{'show-for-medium': graph !== 'risk'}"
        :title="ratingPanelTitle"
      >
        <template #panelHeaderActions>
          <DropdownLink
            v-if="earliestApprovedInspection && ratingParams"
            :items="[{ label: $t('general.csv'), path: ratingParams }]"
            :modifiers="['right-border', !ratingData && 'hide']"
          />
          <DateFilter
            v-if="earliestApprovedInspection"
            id="risk"
            :class="!ratingData && 'hide'"
            :earliestGraphDate="earliestApprovedInspection"
            :modifiers="['no-padding-right']"
            :showCustomDateRange="true"
            @update:model-value="filterGraph($event)"
          />
        </template>
        <DashboardChart
          v-if="ratingData"
          :chartStyle="graphType"
          :series="ratingData"
          :showLegend="isShaftV2"
          @CSVparamUpdate="updateCSVPath"
        />
        <Loader
          v-else
          class="chart-loader-issue"
        />
      </DashboardChartPanel>
    </div>
  </template>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import I18n from 'i18n-js';
import useAjax from '@/functions/use_ajax';
import useOrganisationProfile from '@/functions/use_organisation_profile';

// Components
import DashboardChart from '@/components/charts/dashboard_chart.vue';
import DashboardChartPanel from '@/components/dashboard/dashboard_chart_panel.vue';
import DateFilter from '@/components/form/date_filter.vue';
import DropdownLink from '@/components/generic/download_link.vue';
import Loader from '@/components/generic/loader.vue';
import StackedChart from '@/components/charts/stacked_chart.vue';
import SwitchButton from '@/components/form/switch_button.vue';

const props = defineProps({
  organisationId: { type: String, required: true },
  shaftId: { type: String, required: true },
});

const ajax = useAjax();
const chartFilterDateRange = ref({});
const conditionData = ref(null);
const conditionParams = ref(null);
const earliestApprovedInspection = ref('');
const graph = ref('condition');
const inspectionHistory = ref(null);
const inspectionHistoryLoading = ref(false);
const loading = ref(true);
const ratingChartFilterDateRange = ref({});
const ratingData = ref(null);
const ratingParams = ref(null);
const route = useRoute();

const shaftUrl = computed(() => {
  if (route.name.includes('shaftV2')) {
    return 'shafts_v2/shafts';
  } else {
    return 'shafts';
  }
});

const isShaftV2 = computed(() => route.name.includes('shaftV2'));

const { profileLoaded, showRisk } = useOrganisationProfile();

const graphType = computed(() => {
  if (!isShaftV2.value) return 'risk';
  if (!profileLoaded.value) return null;

  return showRisk.value ? 'risk' : 'priority';
});

// Derived from graphType rather than showRisk so the panel doesnt briefly show
// the wrong heading while the organisation profile is still loading.
const ratingPanelTitle = computed(() => {
  if (!graphType.value) return '';

  return graphType.value === 'risk'
    ? I18n.t('operations.issues_by_risk')
    : I18n.t('operations.issues_by_priority');
});

const graphOptions = computed(() => [
  { label: I18n.t('operations.condition'), value: 'condition' },
  {
    label: graphType.value === 'priority' ? I18n.t('operations.priority') : I18n.t('operations.risk'),
    value: 'risk',
  },
]);

onMounted(async () => {
  load();
});

watch(() => props.shaftId, load);

// Re-runs when the organisation profile arrives after mount, which is when
// graphType first resolves. load() covers the case where it was already there.
watch(graphType, loadRatingGraphData);

function loadRatingGraphData() {
  if (!isShaftV2.value) {
    loadShaftsGraphData('risk', 'apex', ratingData);
    return;
  }

  if (!graphType.value) return;

  loadShaftsV2GraphData(graphType.value, 'apex', ratingData, ratingChartFilterDateRange.value);
}

function load() {
  loading.value = true;
  if (isShaftV2.value) {
    loadShaft();
    loadShaftsV2GraphData('condition', 'apex', conditionData, chartFilterDateRange.value);
    loadRatingGraphData();
    loadInspectionHistoryData();
  } else {
    loadRatingGraphData();
    loadShaftsGraphData('condition', 'apex', conditionData);
  }
  loading.value = false;
}

async function loadShaft() {
  let { ok, data } = await ajax(
    `/organisations/${props.organisationId}/${shaftUrl.value}/${props.shaftId}/dashboard`
  );

  if (ok) {
    earliestApprovedInspection.value = data.earliest_approved_inspection;
  }
}

async function loadShaftsGraphData(type, graph, ref) {
  let { ok, data } = await ajax(
    `/organisations/${props.organisationId}/${shaftUrl.value}/${props.shaftId}/dashboard/graph`,
    { query: { type, graph } }
  );

  if (ok) {
    ref.value = data;
  }
}

async function loadShaftsV2GraphData(type, graph, ref, filterParams) {
  ref.value = null;
  let { ok, data } = await ajax(
    `/organisations/${props.organisationId}/${shaftUrl.value}/${props.shaftId}/graph`,
    {
      query: {
        type,
        graph,
        start_date: filterParams?.from,
        end_date: filterParams?.to,
      },
    }
  );

  if (ok) {
    ref.value = data;
  }
}

async function loadInspectionHistoryData() {
  inspectionHistoryLoading.value = true;
  let { ok, data } = await ajax(
    `/organisations/${props.organisationId}/${shaftUrl.value}/${props.shaftId}/graph`,
    {
      query: {
        type: 'detailed_inspection_history',
      },
    }
  );

  if (ok) {
    inspectionHistory.value = data;
    inspectionHistoryLoading.value = false;
  }
}

function filterGraph({ id, from, to }) {
  if (id === 'condition') {
    chartFilterDateRange.value = { from, to };
    loadShaftsV2GraphData(
      'condition',
      'apex',
      conditionData,
      chartFilterDateRange.value
    );
  } else {
    ratingChartFilterDateRange.value = { from, to };
    loadRatingGraphData();
  }
}

function updateCSVPath(data, type) {
  let endpoint = `/internal/organisations/${props.organisationId}/${shaftUrl.value}/${props.shaftId}/graph/download?type=`;
  if (type === 'condition') {
    let params = data.map((item) => `&conditions[]=${item}`);
    return (conditionParams.value = buildEndPoint(
      endpoint,
      type,
      params,
      startAndEndParam(chartFilterDateRange.value)
    ));
  }
  if (type === 'risk') {
    let params = data.map((item) => `&risk_rating_levels[]=${item}`);
    return (ratingParams.value = buildEndPoint(
      endpoint,
      type,
      params,
      startAndEndParam(ratingChartFilterDateRange.value)
    ));
  }
  if (type === 'priority') {
    let params = data.map((item) => `&priorities[]=${item}`);
    return (ratingParams.value = buildEndPoint(
      endpoint,
      type,
      params,
      startAndEndParam(ratingChartFilterDateRange.value)
    ));
  }
}

function startAndEndParam(rangeType) {
  return Object.keys(rangeType).length !== 0
    ? `&start_date="${rangeType.from}&end_date="${rangeType.to}"`
    : '';
}

function buildEndPoint(endpoint, type, params, startEnd) {
  return `${endpoint}${type}${params.join('')}${startEnd}`;
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';

$graph-height: rem(132);
$graph-height-large: rem(228);

.graph {
  flex-grow: 1;
  margin-bottom: bu(4);
  height: $graph-height;

  @include breakpoint(large) {
    height: $graph-height-large;
    margin-right: 10%;
  }
}
</style>
